'use client';

import {
  getContractDurationFromBundle,
  getProductTypeLabelFromItem,
} from '@storefront/utils/prodRequests';
import TranslatedText from '@storefront/shared-components/translated-text/translated-text';
import { useTranslation } from 'react-i18next';
import { NamespacesI18n } from '@storefront/i18n';
import Accordion from '@storefront/shared-components/accordion/accordion';
import { GlobalsStyles } from '@monetization/hpaip-components-library';
import { env } from 'next-runtime-env';
import { sendAnalytics } from '@storefront/app/analytics/SimpleTriggerEvent';
import { uiEvents } from '@storefront/services/DataCollection/simpleUiEvents';
import { useReactiveVar } from '@apollo/client';
import { configurationState } from '../../printer/state-management/pdp-config-state';
import { getSkuList } from '../../printer/utils/utils';
import { useEffect, useRef, useState } from 'react';
import { useIntersection } from '@storefront/hooks/useIntersection';
import DataCollection from '@storefront/services/DataCollection';
import styles from './disclaimers.module.scss';
import { useProductCancellationList } from '@storefront/hooks/useProductCancellationList';
import { FlowType } from '@storefront/app/flow-orchestrator/types';

interface DisclaimerProps {
  searchParams: {
    skus: string;
    pdpFlow: FlowType;
  };
}

const {
  color: { gray7, white },
} = GlobalsStyles;

export function Disclaimers({ searchParams }: Readonly<DisclaimerProps>) {
  const [triggerAnalyticsDisclaimerOnce, setTriggerAnalyticsDisclaimerOnce] =
    useState(true);

  const { t } = useTranslation(NamespacesI18n.PDP_CONFIG);
  const disclaimerRef = useRef(null);
  const disclaimerInViewport = useIntersection(disclaimerRef, '0px');
  const skus = getSkuList(searchParams.skus);
  const flowType = searchParams?.pdpFlow;
  const { multipleProduct } = useProductCancellationList(skus);
  const configuration = useReactiveVar(configurationState);

  useEffect(() => {
    const productAnalytics = uiEvents.disclaimerPrinterSD;
    if (disclaimerInViewport && triggerAnalyticsDisclaimerOnce) {
      DataCollection.sendEvent(productAnalytics);
      setTriggerAnalyticsDisclaimerOnce(false);
    }
  }, [disclaimerInViewport]);

  const sendAnalyticsHLC = (href: string, controlName: string) => {
    const productAnalytics = uiEvents.disclaimerGenericHLCprinter;
    sendAnalytics({
      simpleUiEvent: { ...productAnalytics, controlName, controlDetail: href },
    });
  };

  const translatedComponents = {
    myAccount: {
      href: env('NEXT_PUBLIC_MY_ACCOUNT'),
      id: 'disclaimer-my-account-link',
      onClick: (event: React.MouseEvent<HTMLAnchorElement>) =>
        sendAnalyticsHLC(event.currentTarget.href, 'MyAccount'),
    },
    hpOnePrivacy: {
      href: t('termsOfService.termsOfService'),
      id: 'disclaimer-hp-one-privacy-link',
      onClick: (event: React.MouseEvent<HTMLAnchorElement>) =>
        sendAnalyticsHLC(event.currentTarget.href, 'TermsOfService'),
    },
    proLiveSupport: {
      href: t('termsOfService.proLiveSupport'),
      id: 'disclaimer-pro-live-support',
      onClick: (event: React.MouseEvent<HTMLAnchorElement>) =>
        sendAnalyticsHLC(event.currentTarget.href, 'ProLiveSupport'),
    },
    nextDayReplacement: {
      href: t('termsOfService.nextDayReplacement'),
      id: 'disclaimer-next-day-replacement',
      onClick: (event: React.MouseEvent<HTMLAnchorElement>) =>
        sendAnalyticsHLC(
          event.currentTarget.href,
          'NextBusinessDayReplacement'
        ),
    },
    mobilePrinting: {
      href: t('termsOfService.mobilePrinting'),
      id: 'disclaimer-mobile-printing',
      onClick: (event: React.MouseEvent<HTMLAnchorElement>) =>
        sendAnalyticsHLC(event.currentTarget.href, 'MobilePrinting'),
    },
    hpAi: {
      href: t('termsOfService.hpAi'),
      id: 'disclaimer-hp-ai',
      onClick: (event: React.MouseEvent<HTMLAnchorElement>) =>
        sendAnalyticsHLC(event.currentTarget.href, 'HpAi'),
    },
  };

  const bundleProduct =
    multipleProduct?.find(
      (product) => product.sku === configuration.activeSku
    ) ?? multipleProduct[0];

  const bundleItemOption = bundleProduct?.items?.find(
    (item) => getProductTypeLabelFromItem(item) === 'leaseReceivable'
  )?.options?.[0];

  const disclaimerType = flowType === FlowType.Pc ? 'pc' : 'printer';
  let iterableDisclaimer: string[] = [];
  const disclaimer = t(`disclaimers.${disclaimerType}`, {
    returnObjects: true,
  });
  if (disclaimer != null && Object.keys(disclaimer).length > 0)
    iterableDisclaimer = Object.values(disclaimer);

  return (
    <div
      ref={disclaimerRef}
      data-testid={'disclaimer'}
      className={styles['disclaimer']}
      id="faq-disclaimer"
    >
      {iterableDisclaimer?.length > 0 && (
        <div>
          <Accordion
            behavior="singleExpand"
            customAppearance="chevron"
            backgroundColor={gray7}
            hideBorder={true}
            textColor={white}
            items={[
              {
                header: {
                  centralArea: (
                    <span className={styles.disclaimerTitle}>
                      {t('modals.modalDisclaimer.disclaimer')}
                    </span>
                  ),
                },
                content: (
                  <div className={styles.disclaimerContainer}>
                    {iterableDisclaimer.length > 0 && (
                      <>
                        <div className={styles.disclaimerFirstRow}>
                          <TranslatedText
                            underline={true}
                            linkStyle="white"
                            i18nKey={iterableDisclaimer[0]}
                            links={translatedComponents}
                            values={{
                              months:
                                getContractDurationFromBundle(bundleItemOption),
                            }}
                          />
                        </div>
                        <ol className={styles.disclaimerList}>
                          {iterableDisclaimer.slice(1).map((item) => (
                            <li key={item} className={styles.disclaimerItem}>
                              <TranslatedText
                                underline={true}
                                linkStyle="white"
                                i18nKey={item}
                                links={translatedComponents}
                                values={{
                                  months:
                                    getContractDurationFromBundle(
                                      bundleItemOption
                                    ),
                                }}
                              />
                            </li>
                          ))}
                        </ol>
                      </>
                    )}
                  </div>
                ),
                id: 'disclaimer-accordion',
              },
            ]}
            data-expanded={false}
            onExpand={() => {
              if (triggerAnalyticsDisclaimerOnce) {
                DataCollection.sendEvent(uiEvents.disclaimerPrinterSD);
                setTriggerAnalyticsDisclaimerOnce(false);
              }
            }}
            onCollapse={() => {}}
          />
        </div>
      )}
    </div>
  );
}
